import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Search } from "lucide-react";

const DECADES = [
    { label: "All", value: "" },
    { label: "1970s", value: "1970" },
    { label: "1980s", value: "1980" },
    { label: "1990s", value: "1990" },
];

export default function Home() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [decade, setDecade] = useState("");
    const [brand, setBrand] = useState("");

    const load = async () => {
        setLoading(true);
        const params = {};
        if (q) params.q = q;
        if (decade) params.decade = Number(decade);
        if (brand) params.manufacturer = brand;
        const { data } = await api.get("/computers", { params });
        setItems(data);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [decade, brand]);

    const brands = useMemo(
        () => Array.from(new Set(items.map((i) => i.manufacturer))).sort(),
        [items]
    );
    const featured = items[0];

    return (
        <div data-testid="home-page" className="mx-auto max-w-[1400px] px-6 lg:px-10">
            {/* Hero */}
            <section className="grid grid-cols-1 md:grid-cols-12 gap-10 pt-12 md:pt-20 pb-16 border-b border-border/60">
                <div className="md:col-span-7 flex flex-col justify-center">
                    <div className="overline mb-6">// Vol. 01 — The Machines That Made Us</div>
                    <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-foreground">
                        A register of<br />
                        <span className="italic text-phosphor-amber glow-amber">
                            silicon relics
                        </span>
                    </h1>
                    <p className="mt-8 font-sans text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        {items.length} curated machines from the pioneer decades of personal computing —
                        their stories, their specifications, and the value the world places on remembering.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-6">
                        <a
                            href="#gallery"
                            data-testid="hero-explore-btn"
                            className="group inline-flex items-center gap-3 border border-phosphor-amber bg-phosphor-amber px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] text-background hover:bg-transparent hover:text-phosphor-amber transition-colors"
                        >
                            Enter the Archive
                            <span className="transition-transform group-hover:translate-x-1">▸</span>
                        </a>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                            <span className="text-phosphor-green">●</span> Live · Updated Feb 2026
                        </div>
                    </div>
                </div>
                <div className="md:col-span-5">
                    {featured && (
                        <Link
                            to={`/computer/${featured.id}`}
                            data-testid="hero-featured-card"
                            className="block relative scanlines border border-border hover:border-phosphor-amber transition-colors group"
                        >
                            {featured.image_url && (
                                <img
                                    src={featured.image_url}
                                    alt={featured.name}
                                    className="w-full h-[380px] md:h-[520px] object-cover grayscale-[.3] group-hover:grayscale-0 transition-all duration-500"
                                />
                            )}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background via-background/70 to-transparent p-6">
                                <div className="overline">// Featured Exhibit</div>
                                <div className="mt-2 flex items-end justify-between">
                                    <div>
                                        <div className="font-serif text-3xl">{featured.name}</div>
                                        <div className="font-mono text-xs text-muted-foreground uppercase tracking-[0.2em] mt-1">
                                            {featured.manufacturer} · {featured.year}
                                        </div>
                                    </div>
                                    <span className="font-mono text-xs text-phosphor-amber">READ ▸</span>
                                </div>
                            </div>
                        </Link>
                    )}
                </div>
            </section>

            {/* Filters */}
            <section id="gallery" className="pt-16 pb-8">
                <div className="flex flex-wrap items-end justify-between gap-6 border-b border-border/60 pb-6">
                    <div>
                        <div className="overline">// 002</div>
                        <h2 className="font-serif text-4xl md:text-5xl mt-2">The Collection</h2>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 border border-border bg-card px-3 py-2">
                            <Search className="h-4 w-4 text-muted-foreground" />
                            <input
                                data-testid="search-input"
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                placeholder="query the archive…"
                                className="bg-transparent font-mono text-xs uppercase tracking-[0.15em] outline-none placeholder:text-muted-foreground/60 w-52"
                            />
                        </div>
                        <select
                            data-testid="filter-decade"
                            value={decade}
                            onChange={(e) => setDecade(e.target.value)}
                            className="border border-border bg-card px-3 py-2 font-mono text-xs uppercase tracking-[0.15em] outline-none focus:border-phosphor-amber"
                        >
                            {DECADES.map((d) => (
                                <option key={d.value} value={d.value}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                        <select
                            data-testid="filter-brand"
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                            className="border border-border bg-card px-3 py-2 font-mono text-xs uppercase tracking-[0.15em] outline-none focus:border-phosphor-amber"
                        >
                            <option value="">All brands</option>
                            {brands.map((b) => (
                                <option key={b} value={b}>
                                    {b}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div data-testid="gallery-loading" className="py-24 text-center font-mono text-xs text-muted-foreground uppercase tracking-[0.3em]">
                        Loading archive<span className="terminal-caret" />
                    </div>
                ) : items.length === 0 ? (
                    <div data-testid="gallery-empty" className="py-24 text-center font-mono text-sm text-muted-foreground">
                        &gt; No records match query.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-8">
                        {items.map((c, idx) => (
                            <Link
                                key={c.id}
                                to={`/computer/${c.id}`}
                                data-testid={`computer-card-${c.id}`}
                                className={`group flex flex-col border border-border hover:border-phosphor-amber transition-colors bg-card ${
                                    idx % 5 === 0 ? "md:col-span-2" : ""
                                }`}
                            >
                                <div className="relative scanlines overflow-hidden">
                                    {c.image_url ? (
                                        <img
                                            src={c.image_url}
                                            alt={c.name}
                                            className={`w-full object-cover grayscale-[.4] group-hover:grayscale-0 transition-all duration-500 ${
                                                idx % 5 === 0 ? "h-[380px]" : "h-[260px]"
                                            }`}
                                        />
                                    ) : (
                                        <div className="grid place-items-center h-[260px] bg-muted font-mono text-xs text-muted-foreground">
                                            NO IMAGE
                                        </div>
                                    )}
                                    <div className="absolute top-3 left-3 border border-phosphor-amber bg-background/80 px-2 py-1 font-mono text-[10px] text-phosphor-amber tracking-[0.2em]">
                                        {c.year}
                                    </div>
                                </div>
                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="overline">{c.manufacturer}</div>
                                    <h3 className="mt-2 font-serif text-2xl text-foreground group-hover:text-phosphor-amber transition-colors">
                                        {c.name}
                                    </h3>
                                    <p className="mt-3 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                                        {c.story}
                                    </p>
                                    <div className="mt-5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground pt-4 border-t border-border/60">
                                        <span>{c.cpu ? c.cpu.split("@")[0].trim() : "—"}</span>
                                        {c.current_value ? (
                                            <span className="text-phosphor-green">
                                                ${Number(c.current_value).toLocaleString()}
                                            </span>
                                        ) : (
                                            <span>—</span>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
